import React, {useEffect, useState} from 'react';
import {ScrollView} from 'react-native';
import {useDebouncedCallback} from 'use-debounce';
import moment from 'moment';
import {connect} from 'react-redux';

import {API_ROOT} from '../../config';
import MessagesService from '../../services/MessagesService';
import ServerSortableSpreadSheet from '../../components/ServerSortableSpreadSheet';
import ReportsSearchSection from '../../components/ReportsSearchSection';
import Pagination from '../../components/Pagination';
import PageContentContainer from '../../components/PageContentContainer';
import ContentSection from '../../components/ContentSection';
import {basicStyles} from '../../styles/basic';

function Inbox(props) {
	const [inboxReport, setInboxReport] = useState([]);
	const [reportCount, setReportCount] = useState(0);
	const [pageCount, setPageCount] = useState(1);
	const [paginationRange, setPaginationRange] = useState([1]);
	const [isLoading, setIsLoading] = useState(true);
	const [sortColumns, setSortColumns] = useState([]);
	const perPage = 10;
	const {organization} = props.auth;
	const {navigate} = props.navigation;

	let today = new Date();
	let startDate = props.route?.params?.startDate ? new Date(props.route.params.startDate).setHours(12) : new Date(new Date().setDate(today.getDate() - 30)).setHours(12, 0, 0, 0);
	let endDate = props.route?.params?.endDate ? new Date(props.route.params.endDate).setHours(12) : today.setHours(12, 0, 0, 0);
	let currentPage = props.route?.params?.currentPage ? parseInt(props.route.params.currentPage) : 1;
	let search = props.route?.params?.search ? props.route.params.search : '';

	const [getInboxReportDebounce] = useDebouncedCallback(() => {
		setIsLoading(true);

		const ordering = sortColumns.map(sc => ({
			orderBy: sc.column,
			order: sc.direction
		}));

		MessagesService.getInboxReport(
			organization.organization_id,
			search,
			startDate ? moment(startDate).format('YYYY-MM-DD') : null,
			endDate ? moment(endDate).format('YYYY-MM-DD') : null,
			perPage,
			currentPage,
			ordering.length > 0 ? ordering : null
		).then(reportData => {
			currentPage = reportData.currentPage;
			setInboxReport(reportData.inboxReport);
			setReportCount(reportData.inboxReportCount);
			setPageCount(reportData.pageCount);
			setPaginationRange(reportData.paginationRange);
			setIsLoading(false);
		});
	}, 500);

	const columns = [
		{
			key: 'name',
			exportName: 'name',
			label: 'Name',
			type: 'text',
			valueExtractor: row => row.name,
			onChangeText() {
			},
			disabled: true,
		},
		{
			key: 'tasks_assigned',
			exportName: 'tasks_assigned',
			label: 'Tasks Assigned',
			type: 'text',
			valueExtractor: row => String(row.tasks_assigned ?? 0),
			onChangeText() {
			},
			disabled: true,
			sortCast: 'int',
		},
		{
			key: 'tasks_resolved',
			exportName: 'tasks_resolved',
			label: 'Tasks Resolved',
			type: 'text',
			valueExtractor: row => String(row.tasks_resolved ?? 0),
			onChangeText() {
			},
			disabled: true,
			sortCast: 'int',
		},
	];

	const handleSortChange = (columnKey) => {
		setSortColumns(prevSortColumns => {
			const existingIndex = prevSortColumns.findIndex(sc => sc.column === columnKey);
			let newSortColumns;

			if (existingIndex >= 0) {
				const existing = prevSortColumns[existingIndex];
				if (existing.direction === 'ASC') {
					newSortColumns = [...prevSortColumns];
					newSortColumns[existingIndex] = {...existing, direction: 'DESC'};
				} else {
					newSortColumns = prevSortColumns.filter((_, index) => index !== existingIndex);
				}
			} else {
				newSortColumns = [...prevSortColumns, {column: columnKey, direction: 'ASC'}];
			}

			navigate('Inbox', {
				...props.route?.params,
				currentPage: 1
			});

			return newSortColumns;
		});
	};

	useEffect(() => {
		getInboxReportDebounce();
	}, [organization.organization_id, search, startDate, endDate, currentPage, JSON.stringify(sortColumns)]);

	return (
		<ScrollView contentContainerStyle={basicStyles.flexScale}>
			<PageContentContainer style={{zIndex: 1}}>
				<ContentSection style={[basicStyles.spreadSheetWrapper, basicStyles.flexScale]}>
					<ServerSortableSpreadSheet
						filterRoute="Inbox"
						rowCount={reportCount}
						rows={inboxReport}
						columns={columns}
						rowKeyExtractor="inbox_report_key"
						allowExport={props.auth.permissions.includes('export_reports')}
						exportUrl={API_ROOT + '/organizations/' + props.auth.organization.organization_id + '/message-feeds/inbox-report?search=' + encodeURIComponent(search) + '&startDate=' + encodeURIComponent(startDate ? moment(startDate).format('YYYY-MM-DD') : '') + '&endDate=' + encodeURIComponent(endDate ? moment(endDate).format('YYYY-MM-DD') : '')}
						exportTitle="inbox-report.csv"
						onSortChange={handleSortChange}
						sortColumns={sortColumns}
						searchElement={
							<ReportsSearchSection
								onChangeSearch={(value) => {
									navigate('Inbox', {
										...props.route?.params,
										currentPage: 1,
										search: value
									});
								}}
								searchValue={search}
								onChangeStartDate={(value) => {
									navigate('Inbox', {
										...props.route?.params,
										startDate: moment(value).format('MM-DD-YYYY'),
										currentPage: 1,
									});
								}}
								startDateValue={startDate}
								onChangeEndDate={(value) => {
									navigate('Inbox', {
										...props.route?.params,
										endDate: moment(value).format('MM-DD-YYYY'),
										currentPage: 1,
									});
								}}
								endDateValue={endDate}
								onChangeDates={(dates) => {
									const [start, end] = dates;
									navigate('Inbox', {
										...props.route?.params,
										startDate: moment(start).format('MM-DD-YYYY'),
										endDate: moment(end).format('MM-DD-YYYY'),
										currentPage: 1
									});
								}}
							/>
						}
						isLoading={isLoading}
					/>
				</ContentSection>
			</PageContentContainer>
			<Pagination screenName="Inbox" pageCount={pageCount} pageIndex={currentPage} paginationRange={paginationRange}/>
		</ScrollView>
	);
}

const mapStateToProps = (state) => {
	const {auth} = state;
	return {auth};
};

export default connect(mapStateToProps)(Inbox);
